import React, { createContext, useContext, useState, useEffect } from 'react';
import { ThemeProvider as MuiThemeProvider, createTheme, responsiveFontSizes } from '@mui/material/styles';
import { themes, defaultTheme, semanticColors } from '../theme/themes';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [currentThemeName, setCurrentThemeName] = useState(() => {
    const savedTheme = localStorage.getItem('theme');
    return savedTheme || defaultTheme;
  });

  const [themeMode, setThemeMode] = useState(() => {
    const savedMode = localStorage.getItem('themeMode');
    return savedMode || 'light';
  });

  // Get the current theme variant (light or dark)
  const currentThemeVariant = themes[currentThemeName][themeMode];

  // Create a responsive Material-UI theme
  const theme = responsiveFontSizes(
    createTheme({
      ...currentThemeVariant,
      components: {
        ...currentThemeVariant.components,
        // Add semantic color tokens
        MuiButton: {
          styleOverrides: {
            root: {
              textTransform: 'none',
            },
          },
        },
      },
      palette: {
        ...currentThemeVariant.palette,
        ...semanticColors,
      },
      typography: {
        ...currentThemeVariant.typography,
        fontFamily: "'Satoshi', 'Roboto', 'Helvetica', 'Arial', sans-serif",
      },
    })
  );

  useEffect(() => {
    // Save theme and mode to localStorage
    localStorage.setItem('theme', currentThemeName);
    localStorage.setItem('themeMode', themeMode);
  }, [currentThemeName, themeMode]);

  const changeTheme = (themeName) => {
    console.log('Changing theme to:', themeName);
    
    // Validate theme name
    if (!themes[themeName]) {
      console.error(`Theme not found: ${themeName}`);
      return;
    }

    setCurrentThemeName(themeName);
  };

  const toggleThemeMode = () => {
    const newMode = themeMode === 'light' ? 'dark' : 'light';
    console.log('Toggling theme mode:', newMode);
    setThemeMode(newMode);
  };

  return (
    <ThemeContext.Provider 
      value={{ 
        currentTheme: {
          ...currentThemeVariant,
          name: currentThemeName,
        }, 
        changeTheme, 
        themeMode, 
        toggleThemeMode 
      }}
    >
      <MuiThemeProvider theme={theme}>
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
}; 